'use client';

import { threads } from '@instamenu/ai-ui';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocale } from '@instamenu/context-i18n';
import { NavItem } from './NavItem';
import { Bot, SquarePen } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { Box } from '@instamenu/ui-system-next';
import * as styles from './Sidebar.css';

export const AiAssistantNav = observer(() => {
  const { i18n } = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  useEffect(() => {
    void threads.fetch().catch(console.error);
    void threads.fetchSavedPrompts().catch(console.error);
  }, []);

  // When navigating to an existing thread via Next.js router, clear the active key
  // so the previous new-chat thread doesn't stay bolded
  useEffect(() => {
    if (pathname.startsWith('/ai/')) {
      threads.setActive(null);
    }
  }, [pathname]);

  const startNewChat = () => {
    threads.setActive(null);
    router.push(`/ai?new=${Date.now()}`);
  };

  const sortedThreads = threads.data.slice().sort((a, b) => b.createdAt - a.createdAt);

  const historyNavItems = sortedThreads.map((thread) => ({
    label: thread.title,
    path: '/ai/' + thread.id,
    key: thread.key,
    pending: thread.pending,
    // activeThreadKey tracks new chats (pathname stays /ai); pathname tracks existing threads
    isActive: thread.key === threads.activeThreadKey || pathname === `/ai/${thread.id}`,
    onDelete: thread.pending
      ? undefined
      : async () => {
          await threads.discard(thread.id);
          if (pathname === `/ai/${thread.id}`) {
            startNewChat();
          }
        },
    onRename: thread.pending
      ? undefined
      : async (title: string) => {
          await threads.rename(thread.id, title);
        },
  }));

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <NavItem
        path="/ai"
        icon={Bot}
        label={i18n.t('mainMenu.newAiChat')}
        isActive={pathname === '/ai'}
        onClick={startNewChat}
        headerAction={
          <button
            type="button"
            aria-label={i18n.t('mainMenu.newAiChat')}
            className={styles.inlineNewChat}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startNewChat();
            }}
          >
            <SquarePen size={16} />
          </button>
        }
      />

      {historyNavItems.length > 0 && (
        <NavItem
          path="/ai"
          subItems={historyNavItems}
          label={i18n.t('mainMenu.chatHistory')}
          alwaysOpen
          isSectionLabel
        />
      )}
    </Box>
  );
});
